import { useEffect, useRef, useState } from "react";
import { Dropdown, Menu, message, TableColumnsType } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { ReactComponent as MenuIcon } from "@assets/images/vertical-dots.svg";

import Table from "@components/Table";
import Button from "@components/Button";
import { checkPermission } from "@utils";
import { permissions } from "@router";
import {
  ErrorServices,
  useDeleteCompanyJobFunctionMutation,
  useFetchPaginatedCompanyJobFunctionsQuery,
  useDownloadCompanyJobFunctionsMutation,
  useUploadCompanyJobFunctionsMutation,
  useFetchCompaniesQuery,
} from "@services";
import AddCompanyJobFunction from "./AddJobFunction";
import EditCompanyJobFunction from "./EditJobFunction";
import { useTypedSelector } from "@/hooks";
// import { handleCsvUpload } from "@/utils/uploadCsv";
import CompanyList from "@/components/List/company-list";
import Upload from "@/components/Upload";

type JobSubFunctionType = {
  id: number;
  name: string;
  description?: string | null;
};

type JobFunctionType = {
  id: number;
  name: string;
  description?: string | null;
  sub_job_functions: Array<JobSubFunctionType>;
};

const CompanyJobFunction = () => {
  const dispatch = useDispatch();
  const { user, permissions: per } = useTypedSelector(state => state.auth);
  const { company_id } = useParams<{ company_id: string }>();
  const companyID = company_id || user?.company?.id;
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const jf_id = useRef<any>(null);
  // const inputRef = useRef<any>(null);

  const [isAddJFVisible, setIsAddJFVisible] = useState(false);
  const [isEditJFVisible, setIsEditJFVisible] = useState(false);
  const [jfItem, setJfItem] = useState<JobFunctionType | {}>({});

  const [companyId, setCompanyId] = useState<number | null>(Number(companyID));
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies = companiesData?.data || [];

  const { data: jobFunctionsData, isLoading: isLoadingJobFunctions } =
    useFetchPaginatedCompanyJobFunctionsQuery(
      {
        company_id: companyId!,
        page,
        page_size: pageSize,
      },
      {
        skip: !companyId, // ✅ added
      }
    );

  const jobFunctions = jobFunctionsData?.data || [];
  const pagination = jobFunctionsData?.pagination;

  const [deleteJobFunction] = useDeleteCompanyJobFunctionMutation();
  const [uploadJobFunction, { isLoading: isUploading }] =
    useUploadCompanyJobFunctionsMutation();

  const [downloadJobFunctions, { isLoading }] =
    useDownloadCompanyJobFunctionsMutation();

  const handleActionDropdown = ({
    item,
    key,
    domEvent,
  }: {
    key: string;
    domEvent:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>;
    item: JobFunctionType;
  }) => {
    domEvent.stopPropagation();

    if (key === "2") {
      removeJobFunction(item?.id.toString());
    } else {
      onEditJf(item);
    }
  };

  const columns: TableColumnsType<JobFunctionType> = [
    {
      title: "Company Job Functions",
      key: "name",
      dataIndex: "name",
      width: "20%",
    },
    {
      title: "Company Job Sub-Functions",
      key: "sub_job_functions",
      width: "55%",
      render: ({ sub_job_functions }) => {
        if (Array.isArray(sub_job_functions) && sub_job_functions.length) {
          return sub_job_functions
            .map((val: JobSubFunctionType) => val.name)
            .join(", ");
        }
        return null;
      },
    },
    ...((checkPermission(permissions.UPDATE_COMPANY_JOB_FUNCTION) ||
    checkPermission(permissions.DELETE_COMPANY_JOB_FUNCTION)
      ? [
          {
            title: "Actions",
            key: "action",
            fixed: "right",
            width: "10%",
            render: (item: JobFunctionType) => {
              const menu = (
                <Menu
                  onClick={(info: any) =>
                    handleActionDropdown({
                      item,
                      key: info.key,
                      domEvent: info.domEvent,
                    })
                  }
                  tabIndex={1}
                >
                  {checkPermission(permissions.UPDATE_COMPANY_JOB_FUNCTION) && (
                    <Menu.Item key="1">Edit</Menu.Item>
                  )}

                  {checkPermission(permissions.DELETE_COMPANY_JOB_FUNCTION) && (
                    <Menu.Item key="2" danger>
                      Delete
                    </Menu.Item>
                  )}
                </Menu>
              );

              return (
                <div className="table__action__menu">
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <MenuIcon
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </Dropdown>
                </div>
              );
            },
          },
        ]
      : []) as any),
  ];

  const onRowClick = (data: JobFunctionType) => {
    history.push(
      `/company-job-functions/company/${companyId}/job_function/${data.id}`,
      {
        companyId: companyId,
      }
    );
  };

  const onEditJf = async (item: JobFunctionType) => {
    try {
      setJfItem(item);
      setIsEditJFVisible(true);
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  const removeJobFunction = async (id: string) => {
    try {
      jf_id.current = id;

      await deleteJobFunction({
        company_id: companyId,
        company_job_function_id: id,
      }).unwrap();

      message.success("Company Job Function Deleted Successfully");
    } catch (err: any) {
      ErrorServices(err);
    }
  };

  // const handleUploadClick = () => {
  //   if (isUploading) return;

  //   if (inputRef.current) {
  //     inputRef.current.value = ""; // reset (important)
  //     inputRef.current.click();
  //   }
  // };

  const handleUpload = async () => {
    if (!companyId || !uploadFile) return;

    try {
      const formData = new FormData();
      formData.append("attachment", uploadFile, uploadFile.name);

      const res = await uploadJobFunction({
        company_id: companyId,
        formData,
      }).unwrap();

      message.success(res?.message || "Uploaded successfully");
    } catch (error: any) {
      ErrorServices(error);
    }
  };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!companyId || isUploading) return;

  //   const file = e.target.files?.[0];

  //   // ❌ No file
  //   if (!file) {
  //     message.error("No file selected");
  //     return;
  //   }

  //   // ❌ Empty file (0 bytes)
  //   if (file.size === 0) {
  //     message.error("File is empty. Please upload a valid CSV.");
  //     return;
  //   }

  //   const reader = new FileReader();

  //   reader.onload = event => {
  //     const text = event.target?.result as string;

  //     const rows = text
  //       .split(/\r?\n/)
  //       .map(row => row.trim())
  //       .filter(row => row.length > 0);

  //     // ❌ Only headers or no real data
  //     if (rows.length <= 1) {
  //       message.error("CSV has no data rows");
  //       return;
  //     }

  //     // ✅ Proceed upload
  //     handleCsvUpload(
  //       e,
  //       formData =>
  //         uploadJobFunction({
  //           company_id: companyId,
  //           formData,
  //         }),
  //       {
  //         loadingMessage: "Uploading job functions...",
  //         successMessage: "Job Functions uploaded successfully",
  //       }
  //     );
  //   };

  //   reader.readAsText(file);
  // };

  const downloadFile = () => {
    if (!companyId || isLoading) return;

    message.loading({
      content: "Downloading Job Functions...",
      key: "job-download",
      duration: 0,
    });

    downloadJobFunctions({ company_id: companyId })
      .unwrap()
      .then(() => {
        message.success({
          content: "Download Started",
          key: "job-download",
        });
      })
      .catch((err: any) => {
        message.destroy("job-download");
        ErrorServices(err);
      });
  };

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        <div className="flex justify-between items-center mt-6 px-4">
          <h2 className="font-bold text-xl text-themeBlue text-left">
            Select the Company
          </h2>
        </div>

        {isFetchingCompanies ? (
          <div className="flex justify-center items-center h-[400px]">
            <LoadingOutlined style={{ fontSize: 60 }} />
          </div>
        ) : (
          <main className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full">
              <CompanyList
                companiesList={companies}
                onSelectCompany={setSelectedCompany}
                selectedCompany={selectedCompany}
              />

              {/* ✅ SAME ORIGINAL BUTTON BEHAVIOR */}
              {selectedCompany && (
                <div className="mt-8 flex gap-2 sticky bottom-2">
                  <button
                    onClick={() => setCompanyId(selectedCompany)}
                    className="flex-1 bg-themeBlue text-white px-5 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    {
                      companies.find((item: any) => item.id == selectedCompany)
                        ?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      <AddCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsAddJFVisible}
        isVisible={isAddJFVisible}
      />

      <EditCompanyJobFunction
        companyId={companyId}
        setIsVisible={setIsEditJFVisible}
        isVisible={isEditJFVisible}
        jfItem={jfItem}
      />

      <div className="appTitle text-base font-[500] mx-5">
        Company Job Functions
      </div>

      <div className="jobCreationBar sticky top-0 z-10 bg-white px-14 py-6 rounded-lg flex justify-between items-center my-7">
        <div className="title text-themeBlue font-[500] text-3xl">
          Company Job Functions & Sub Functions
        </div>

        {checkPermission(permissions.CREATE_COMPANY_JOB_FUNCTION) && (
          <div className="action-buttons flex items-center gap-2">
            {isUploadModal && (
              <Upload
                title="Upload Job Functions"
                onSubmit={handleUpload}
                isVisible={isUploadModal}
                setIsVisible={setIsUploadModal}
                file={uploadFile}
                setFile={setUploadFile}
              />
            )}

            {/* <input
              id="myInput"
              type="file"
              ref={inputRef}
              hidden={true}
              accept=".csv,text/csv"
              onChange={handleFileUpload}
            /> */}

            {/* <Button
              variant="upload"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              Upload Job Functions
            </Button> */}

            {checkPermission(permissions.CREATE_COMPANY_JOB_FUNCTION) && (
              <Button
                variant="upload"
                onClick={() => setIsUploadModal(true)}
                disabled={isUploading}
              >
                Upload Job Functions
              </Button>
            )}

            {checkPermission(permissions.CREATE_COMPANY_JOB_FUNCTION) && (
              <Button variant="add" onClick={() => setIsAddJFVisible(true)}>
                Add New Job
              </Button>
            )}
          </div>
        )}

        <Button variant="download" onClick={downloadFile} disabled={isLoading}>
          Download Job Functions
        </Button>
      </div>

      <div className="bg-white px-14 py-6 rounded-lg">
        <Table
          data={jobFunctions}
          columns={columns}
          onRowClick={onRowClick}
          isLoading={isLoadingJobFunctions}
          pagination={true}
          count={pagination?.count}
          onChangePage={setPage}
          onPageSizeChange={setPageSize}
          page={page}
        />
      </div>
    </>
  );
};

export default CompanyJobFunction;
